import { useState, useEffect, useCallback, useRef } from "react";

const ADMIN_EDGE_URL = "https://bpzsocjzlwyssgxidrkm.supabase.co/functions/v1/admin-invite-codes";
const ADMIN_KEY_STORAGE = "maverick_admin_key";
const AUTO_REFRESH_INTERVAL = 30 * 1000;

interface InviteCode {
  id: string;
  code: string;
  label: string | null;
  max_uses: number;
  use_count: number;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  expires_at: string | null;
}

interface AccessLog {
  id: string;
  code_used: string;
  fingerprint: string | null;
  user_agent: string | null;
  accessed_at: string;
  invite_codes: { code: string; label: string | null } | null;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("ja-JP", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  if (mins < 1) return "たった今";
  if (mins < 60) return `${mins}分前`;
  if (hours < 24) return `${hours}時間前`;
  return `${Math.floor(hours / 24)}日前`;
}

function isRecent(iso: string, thresholdMs = 5 * 60 * 1000): boolean {
  return Date.now() - new Date(iso).getTime() < thresholdMs;
}

// ── ライブパルスドット ──────────────────────────────────
function LiveDot({ active }: { active: boolean }) {
  return (
    <span className="relative inline-flex items-center justify-center w-2 h-2">
      {active && (
        <span
          className="absolute inline-flex w-full h-full rounded-full"
          style={{ background: "rgba(201,168,76,0.4)", animation: "livePing 1.4s ease-out infinite" }}
        />
      )}
      <span
        className="relative inline-flex rounded-full w-1.5 h-1.5"
        style={{ background: active ? "rgba(201,168,76,0.9)" : "rgba(255,255,255,0.15)" }}
      />
    </span>
  );
}

// ── カウントダウンバー ─────────────────────────────────
function RefreshCountdown({ intervalMs, lastRefreshAt, onRefreshNow }: {
  intervalMs: number;
  lastRefreshAt: number;
  onRefreshNow: () => void;
}) {
  const [pct, setPct] = useState(100);

  useEffect(() => {
    const tick = () => {
      const elapsed = Date.now() - lastRefreshAt;
      setPct(Math.max(0, 100 - (elapsed / intervalMs) * 100));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [lastRefreshAt, intervalMs]);

  return (
    <div className="flex items-center gap-3">
      <div className="w-24 h-px relative overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
        <div
          className="absolute left-0 top-0 h-full transition-none"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(to right, rgba(201,168,76,0.2), rgba(201,168,76,0.5))",
          }}
        />
      </div>
      <button
        onClick={onRefreshNow}
        className="text-xs tracking-[0.2em] cursor-pointer whitespace-nowrap"
        style={{ color: "rgba(255,255,255,0.2)", background: "none", border: "none", transition: "color 0.3s" }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.5)"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.2)"; }}
      >
        今すぐ更新
      </button>
    </div>
  );
}

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) ?? "");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [tab, setTab] = useState<"codes" | "logs">("codes");

  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(Date.now());
  const [prevLogIds, setPrevLogIds] = useState<Set<string>>(new Set());
  const [newLogIds, setNewLogIds] = useState<Set<string>>(new Set());
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 新規発行フォーム
  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newMaxUses, setNewMaxUses] = useState(3);
  const [newCreatedBy, setNewCreatedBy] = useState("");
  const [newExpiresAt, setNewExpiresAt] = useState("");
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState("");

  const headers = { "Content-Type": "application/json", "x-admin-key": adminKey };

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${ADMIN_EDGE_URL}?action=list`, { headers });
      if (res.status === 401) { setAuthed(false); return; }
      const data = await res.json();
      setCodes(data.codes ?? []);
      setLastRefreshAt(Date.now());
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  const fetchLogs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${ADMIN_EDGE_URL}?action=logs`, { headers });
      if (res.status === 401) { setAuthed(false); return; }
      const data = await res.json();
      const incoming: AccessLog[] = data.logs ?? [];

      // 新着検出
      setPrevLogIds((prev) => {
        if (prev.size > 0) {
          const fresh = new Set(incoming.map((l) => l.id).filter((id) => !prev.has(id)));
          if (fresh.size > 0) {
            setNewLogIds(fresh);
            setTimeout(() => setNewLogIds(new Set()), 8000);
          }
        }
        return new Set(incoming.map((l) => l.id));
      });

      setLogs(incoming);
      setLastRefreshAt(Date.now());
    } finally {
      if (!silent) setLoading(false);
    }
  }, [adminKey]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(false);
    const res = await fetch(`${ADMIN_EDGE_URL}?action=list`, {
      headers: { "Content-Type": "application/json", "x-admin-key": adminKey },
    });
    if (res.ok) {
      localStorage.setItem(ADMIN_KEY_STORAGE, adminKey);
      setAuthed(true);
      const data = await res.json();
      setCodes(data.codes ?? []);
      setLastRefreshAt(Date.now());
    } else {
      setAuthError(true);
    }
  };

  // 初回ロード
  useEffect(() => {
    if (!authed) return;
    if (tab === "codes") fetchCodes();
    else fetchLogs();
  }, [authed, tab]);

  // 30秒自動更新
  useEffect(() => {
    if (!authed) return;
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    autoRefreshRef.current = setInterval(() => {
      if (tab === "codes") fetchCodes();
      else fetchLogs(true);
    }, AUTO_REFRESH_INTERVAL);
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    };
  }, [authed, tab, fetchCodes, fetchLogs]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCode.trim()) return;
    setCreating(true);
    setCreateMsg("");
    try {
      const res = await fetch(`${ADMIN_EDGE_URL}?action=create`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          code: newCode.trim().toUpperCase(),
          label: newLabel || null,
          max_uses: newMaxUses,
          created_by: newCreatedBy || null,
          expires_at: newExpiresAt || null,
        }),
      });
      const data = await res.json();
      if (data.code) {
        setCreateMsg(`✓ コード「${data.code.code}」を発行しました`);
        setNewCode(""); setNewLabel(""); setNewCreatedBy(""); setNewExpiresAt("");
        fetchCodes();
      } else {
        setCreateMsg(`✗ エラー: ${data.error ?? "不明なエラー"}`);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (id: string, current: boolean) => {
    await fetch(`${ADMIN_EDGE_URL}?action=toggle`, {
      method: "PATCH", headers,
      body: JSON.stringify({ id, is_active: !current }),
    });
    fetchCodes();
  };

  const handleReset = async (id: string) => {
    await fetch(`${ADMIN_EDGE_URL}?action=reset`, {
      method: "PATCH", headers,
      body: JSON.stringify({ id }),
    });
    fetchCodes();
  };

  const gold = "rgba(201,168,76,";

  // ── 統計サマリー ────────────────────────────────────
  const totalCodes = codes.length;
  const activeCodes = codes.filter((c) => c.is_active).length;
  const totalUses = codes.reduce((s, c) => s + c.use_count, 0);
  const recentLogs = logs.filter((l) => isRecent(l.accessed_at, 60 * 60 * 1000));

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0a0f" }}>
        <div className="w-full max-w-sm px-8">
          <div className="text-center mb-10">
            <div className="text-xs tracking-[0.7em] uppercase mb-3" style={{ color: `${gold}0.3)`, fontFamily: "'Cormorant Garamond', serif" }}>
              MAVERICK
            </div>
            <h1 className="text-2xl font-light" style={{ color: "rgba(255,255,255,0.6)", fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.1em" }}>
              管理者認証
            </h1>
          </div>
          <form onSubmit={handleAuth} className="flex flex-col gap-4">
            <input
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              placeholder="管理者キーを入力"
              className="w-full bg-transparent text-center outline-none py-3 text-sm"
              style={{
                color: "rgba(255,255,255,0.5)", fontFamily: "'Cormorant Garamond', serif",
                letterSpacing: "0.3em",
                borderBottom: `1px solid ${authError ? "rgba(180,60,60,0.4)" : "rgba(201,168,76,0.2)"}`,
                borderTop: "none", borderLeft: "none", borderRight: "none",
              }}
            />
            {authError && (
              <p className="text-center text-xs" style={{ color: "rgba(180,60,60,0.7)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                認証に失敗した。
              </p>
            )}
            <button
              type="submit"
              className="mt-4 py-3 text-xs uppercase tracking-[0.4em] cursor-pointer whitespace-nowrap"
              style={{
                background: "transparent", border: `1px solid ${gold}0.2)`,
                color: `${gold}0.5)`, fontFamily: "'Cormorant Garamond', serif", transition: "border-color 0.3s, color 0.3s",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = `${gold}0.5)`; (e.currentTarget as HTMLElement).style.color = `${gold}0.8)`; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = `${gold}0.2)`; (e.currentTarget as HTMLElement).style.color = `${gold}0.5)`; }}
            >
              ENTER
            </button>
          </form>
        </div>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap');`}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#0a0a0f", fontFamily: "'Cormorant Garamond', serif" }}>
      {/* ヘッダー */}
      <div className="border-b" style={{ borderColor: `${gold}0.1)` }}>
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <span className="text-xs tracking-[0.5em] uppercase" style={{ color: `${gold}0.3)` }}>MAVERICK</span>
            <span className="text-sm" style={{ color: "rgba(255,255,255,0.3)" }}>招待コード管理</span>
            <div className="flex items-center gap-1.5">
              <LiveDot active={true} />
              <span className="text-xs" style={{ color: "rgba(255,255,255,0.15)" }}>LIVE</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <RefreshCountdown
              intervalMs={AUTO_REFRESH_INTERVAL}
              lastRefreshAt={lastRefreshAt}
              onRefreshNow={() => { if (tab === "codes") fetchCodes(); else fetchLogs(); }}
            />
            <button
              onClick={() => { setAuthed(false); localStorage.removeItem(ADMIN_KEY_STORAGE); }}
              className="text-xs tracking-[0.3em] cursor-pointer whitespace-nowrap"
              style={{ color: "rgba(255,255,255,0.2)", background: "none", border: "none", transition: "color 0.3s" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.5)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.2)"; }}
            >
              LOGOUT
            </button>
          </div>
        </div>
      </div>

      {/* 統計サマリー */}
      <div className="border-b" style={{ borderColor: `${gold}0.06)` }}>
        <div className="max-w-5xl mx-auto px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "発行済みコード", value: totalCodes, sub: "total" },
            { label: "有効コード", value: activeCodes, sub: "active" },
            { label: "累計入場回数", value: totalUses, sub: "uses" },
            { label: "直近1時間の入場", value: recentLogs.length, sub: "recent", highlight: recentLogs.length > 0 },
          ].map((stat, i) => (
            <div key={i} className="flex flex-col gap-0.5">
              <span
                className="text-2xl font-light"
                style={{
                  color: stat.highlight ? `${gold}0.9)` : "rgba(255,255,255,0.5)",
                  fontFamily: "'Cormorant Garamond', serif",
                  textShadow: stat.highlight ? `0 0 20px rgba(201,168,76,0.3)` : "none",
                  transition: "color 0.5s ease",
                }}
              >
                {stat.value}
              </span>
              <span className="text-xs tracking-[0.15em]" style={{ color: "rgba(255,255,255,0.2)" }}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* タブ */}
        <div className="flex gap-8 mb-8 border-b" style={{ borderColor: `${gold}0.08)` }}>
          {(["codes", "logs"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="pb-3 text-sm tracking-[0.3em] uppercase cursor-pointer whitespace-nowrap flex items-center gap-2"
              style={{
                background: "none", border: "none",
                color: tab === t ? `${gold}0.7)` : "rgba(255,255,255,0.2)",
                borderBottom: tab === t ? `1px solid ${gold}0.4)` : "1px solid transparent",
                transition: "color 0.3s",
              }}
            >
              {t === "codes" ? "コード一覧" : (
                <>
                  アクセスログ
                  {newLogIds.size > 0 && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-full"
                      style={{ background: `${gold}0.2)`, color: `${gold}0.9)`, fontSize: "10px" }}
                    >
                      NEW
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </div>

        {tab === "codes" && (
          <div className="flex flex-col gap-8">
            {/* 新規発行フォーム */}
            <div className="p-6" style={{ border: `1px solid ${gold}0.1)`, background: "rgba(201,168,76,0.02)" }}>
              <h2 className="text-sm tracking-[0.4em] uppercase mb-6" style={{ color: `${gold}0.4)` }}>新規コード発行</h2>
              <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { label: "コード *", value: newCode, onChange: (v: string) => setNewCode(v.toUpperCase()), placeholder: "PHOENIX", mono: true },
                  { label: "ラベル（誰に発行）", value: newLabel, onChange: (v: string) => setNewLabel(v), placeholder: "田中様" },
                  { label: "発行者", value: newCreatedBy, onChange: (v: string) => setNewCreatedBy(v), placeholder: "管理者" },
                ].map((field, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    <label className="text-xs tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.2)" }}>{field.label}</label>
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder={field.placeholder}
                      className="bg-transparent outline-none py-2 text-sm"
                      style={{
                        color: "rgba(255,255,255,0.6)",
                        borderBottom: `1px solid ${gold}0.15)`, borderTop: "none", borderLeft: "none", borderRight: "none",
                        letterSpacing: field.mono ? "0.2em" : undefined,
                      }}
                    />
                  </div>
                ))}
                <div className="flex flex-col gap-1">
                  <label className="text-xs tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.2)" }}>使用上限回数</label>
                  <input
                    type="number" min={1} max={100} value={newMaxUses}
                    onChange={(e) => setNewMaxUses(Number(e.target.value))}
                    className="bg-transparent outline-none py-2 text-sm"
                    style={{ color: "rgba(255,255,255,0.6)", borderBottom: `1px solid ${gold}0.15)`, borderTop: "none", borderLeft: "none", borderRight: "none" }}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.2)" }}>有効期限（任意）</label>
                  <input
                    type="datetime-local" value={newExpiresAt}
                    onChange={(e) => setNewExpiresAt(e.target.value)}
                    className="bg-transparent outline-none py-2 text-sm"
                    style={{ color: "rgba(255,255,255,0.4)", borderBottom: `1px solid ${gold}0.15)`, borderTop: "none", borderLeft: "none", borderRight: "none", colorScheme: "dark" }}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={creating || !newCode.trim()}
                    className="w-full py-2 text-xs tracking-[0.3em] uppercase cursor-pointer whitespace-nowrap"
                    style={{
                      background: "transparent", border: `1px solid ${gold}0.25)`, color: `${gold}0.6)`,
                      opacity: creating || !newCode.trim() ? 0.4 : 1, transition: "all 0.3s",
                    }}
                    onMouseEnter={(e) => { if (!creating && newCode.trim()) { (e.currentTarget as HTMLElement).style.borderColor = `${gold}0.5)`; (e.currentTarget as HTMLElement).style.color = `${gold}0.9)`; } }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = `${gold}0.25)`; (e.currentTarget as HTMLElement).style.color = `${gold}0.6)`; }}
                  >
                    {creating ? "···" : "発行する"}
                  </button>
                </div>
              </form>
              {createMsg && (
                <p className="mt-4 text-xs tracking-[0.2em]" style={{ color: createMsg.startsWith("✓") ? `${gold}0.6)` : "rgba(180,60,60,0.7)", fontStyle: "italic" }}>
                  {createMsg}
                </p>
              )}
            </div>

            {/* コード一覧 */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm tracking-[0.4em] uppercase" style={{ color: `${gold}0.4)` }}>
                  発行済みコード
                  <span className="ml-3 text-xs" style={{ color: "rgba(255,255,255,0.2)" }}>({codes.length})</span>
                </h2>
              </div>

              {loading ? (
                <p className="text-xs text-center py-8" style={{ color: "rgba(255,255,255,0.15)", fontStyle: "italic" }}>読み込み中···</p>
              ) : codes.length === 0 ? (
                <p className="text-xs text-center py-8" style={{ color: "rgba(255,255,255,0.1)", fontStyle: "italic" }}>コードがまだ発行されていない</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {codes.map((c) => {
                    const usePct = Math.min(100, (c.use_count / c.max_uses) * 100);
                    const isExhausted = c.use_count >= c.max_uses;
                    const isExpired = !!(c.expires_at && new Date(c.expires_at) < new Date());
                    return (
                      <div
                        key={c.id}
                        className="flex flex-col sm:flex-row sm:items-center gap-3 p-4"
                        style={{
                          border: `1px solid ${c.is_active && !isExhausted ? `${gold}0.1)` : "rgba(255,255,255,0.04)"}`,
                          background: c.is_active ? "rgba(201,168,76,0.02)" : "rgba(255,255,255,0.01)",
                          opacity: c.is_active && !isExhausted ? 1 : 0.45,
                          transition: "all 0.3s ease",
                        }}
                      >
                        <div className="flex-1 flex flex-col gap-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-sm tracking-[0.3em]" style={{ color: c.is_active ? `${gold}0.8)` : "rgba(255,255,255,0.3)" }}>
                              {c.code}
                            </span>
                            {c.label && <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>— {c.label}</span>}
                            {!c.is_active && <span className="text-xs tracking-[0.2em]" style={{ color: "rgba(180,60,60,0.5)" }}>無効</span>}
                            {isExpired && <span className="text-xs tracking-[0.2em]" style={{ color: "rgba(180,60,60,0.5)" }}>期限切れ</span>}
                            {isExhausted && <span className="text-xs tracking-[0.2em]" style={{ color: "rgba(180,100,60,0.6)" }}>上限到達</span>}
                          </div>
                          <div className="flex items-center gap-4 flex-wrap">
                            <span className="text-xs" style={{ color: "rgba(255,255,255,0.2)" }}>
                              使用: {c.use_count} / {c.max_uses}
                            </span>
                            {c.created_by && <span className="text-xs" style={{ color: "rgba(255,255,255,0.15)" }}>発行者: {c.created_by}</span>}
                            <span className="text-xs" style={{ color: "rgba(255,255,255,0.1)" }}>{formatDate(c.created_at)}</span>
                            {c.expires_at && <span className="text-xs" style={{ color: "rgba(255,255,255,0.1)" }}>期限: {formatDate(c.expires_at)}</span>}
                          </div>
                        </div>

                        {/* 使用状況バー */}
                        <div className="w-full sm:w-24 h-px relative" style={{ background: "rgba(255,255,255,0.05)" }}>
                          <div
                            className="absolute left-0 top-0 h-full transition-all duration-700"
                            style={{
                              width: `${usePct}%`,
                              background: isExhausted
                                ? "rgba(180,60,60,0.6)"
                                : usePct >= 60
                                ? "rgba(220,140,40,0.5)"
                                : `${gold}0.4)`,
                            }}
                          />
                        </div>

                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleReset(c.id)}
                            className="text-xs tracking-[0.2em] cursor-pointer whitespace-nowrap"
                            style={{ color: "rgba(255,255,255,0.2)", background: "none", border: "none", transition: "color 0.3s" }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.5)"; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.2)"; }}
                          >
                            リセット
                          </button>
                          <button
                            onClick={() => handleToggle(c.id, c.is_active)}
                            className="text-xs tracking-[0.2em] cursor-pointer whitespace-nowrap"
                            style={{ color: c.is_active ? "rgba(180,60,60,0.5)" : `${gold}0.4)`, background: "none", border: "none", transition: "color 0.3s" }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = "0.8"; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                          >
                            {c.is_active ? "無効化" : "有効化"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "logs" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-sm tracking-[0.4em] uppercase" style={{ color: `${gold}0.4)` }}>
                  アクセスログ
                  <span className="ml-3 text-xs" style={{ color: "rgba(255,255,255,0.2)" }}>({logs.length})</span>
                </h2>
                {newLogIds.size > 0 && (
                  <span
                    className="text-xs px-2 py-0.5"
                    style={{
                      background: `${gold}0.15)`,
                      border: `1px solid ${gold}0.3)`,
                      color: `${gold}0.8)`,
                      animation: "fadeInUp 0.4s ease",
                    }}
                  >
                    +{newLogIds.size} 新着
                  </span>
                )}
              </div>
            </div>

            {loading ? (
              <p className="text-xs text-center py-8" style={{ color: "rgba(255,255,255,0.15)", fontStyle: "italic" }}>読み込み中···</p>
            ) : logs.length === 0 ? (
              <p className="text-xs text-center py-8" style={{ color: "rgba(255,255,255,0.1)", fontStyle: "italic" }}>ログがまだない</p>
            ) : (
              <div className="flex flex-col gap-0">
                {logs.map((log) => {
                  const isNew = newLogIds.has(log.id);
                  const recent = isRecent(log.accessed_at, 5 * 60 * 1000);
                  return (
                    <div
                      key={log.id}
                      className="flex flex-col sm:flex-row sm:items-center gap-2 py-3 px-4 transition-all duration-700"
                      style={{
                        borderBottom: "1px solid rgba(255,255,255,0.03)",
                        background: isNew
                          ? "rgba(201,168,76,0.06)"
                          : recent
                          ? "rgba(201,168,76,0.02)"
                          : "transparent",
                        borderLeft: isNew
                          ? `2px solid rgba(201,168,76,0.5)`
                          : recent
                          ? `2px solid rgba(201,168,76,0.2)`
                          : "2px solid transparent",
                      }}
                    >
                      <div className="flex-1 flex flex-col gap-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          {isNew && <LiveDot active={true} />}
                          <span className="text-sm tracking-[0.2em]" style={{ color: isNew ? `${gold}0.9)` : `${gold}0.6)` }}>
                            {log.code_used}
                          </span>
                          {log.invite_codes?.label && (
                            <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
                              {log.invite_codes.label}
                            </span>
                          )}
                          {isNew && (
                            <span
                              className="text-xs px-1.5 py-0.5"
                              style={{ background: `${gold}0.15)`, color: `${gold}0.8)`, fontSize: "10px", letterSpacing: "0.1em" }}
                            >
                              NEW
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 flex-wrap">
                          <span className="text-xs" style={{ color: recent ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.15)" }}>
                            {timeAgo(log.accessed_at)}
                          </span>
                          <span className="text-xs" style={{ color: "rgba(255,255,255,0.1)" }}>
                            {formatDate(log.accessed_at)}
                          </span>
                          {log.fingerprint && (
                            <span className="text-xs font-mono" style={{ color: "rgba(255,255,255,0.08)" }}>
                              fp: {log.fingerprint}
                            </span>
                          )}
                        </div>
                      </div>
                      {log.user_agent && (
                        <div className="text-xs max-w-xs truncate" style={{ color: "rgba(255,255,255,0.08)" }} title={log.user_agent}>
                          {log.user_agent.slice(0, 60)}···
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap');
        input[type="number"]::-webkit-inner-spin-button { opacity: 0.3; }
        @keyframes livePing {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
